import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiClient as defaultClient, type ApiClient, type ProjectSummary, type RobotsPolicy } from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/Button';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { ProjectForm, type ProjectValues } from './ProjectForm';
import { AuthedBrand } from './AuthedBrand';
import styles from './ProjectScreen.module.css';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; project: ProjectSummary };

export interface EditProjectProps {
  client?: ApiClient;
}

export function EditProject({ client = defaultClient }: EditProjectProps) {
  const { currentOrg } = useOrg();
  const { id } = useParams<{ id: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [robotsBusy, setRobotsBusy] = useState(false);
  const [robotsMessage, setRobotsMessage] = useState<string | null>(null);
  const canWrite = currentOrg?.role === 'org_admin' || currentOrg?.role === 'editor';

  const load = useCallback(async () => {
    if (!orgId || !id) return;
    setState({ phase: 'loading' });
    try {
      const project = await client.getProject(orgId, id);
      setState({ phase: 'ready', project });
    } catch (err) {
      setState({ phase: 'error', message: toDisplayError(err).message });
    }
  }, [client, orgId, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (values: ProjectValues) => {
    if (!orgId || !id) return;
    const updated = await client.updateProject(orgId, id, values);
    setState({ phase: 'ready', project: updated });
  };

  // #421 — robots.txt stance. Override is a verified-owner action; the server
  // enforces the verified-gate (409) and audit-logs the transition.
  const setRobotsPolicy = useCallback(
    async (policy: RobotsPolicy) => {
      if (!orgId || !id || robotsBusy) return;
      setRobotsBusy(true);
      setRobotsMessage(null);
      try {
        const updated = await client.updateProject(orgId, id, { robotsPolicy: policy });
        setState({ phase: 'ready', project: updated });
        setRobotsMessage(
          policy === 'override'
            ? 'Override saved — TestCraft will ignore robots.txt for this domain.'
            : 'Restored — TestCraft will honor robots.txt again.',
        );
      } catch (err) {
        setRobotsMessage(toDisplayError(err).message);
      } finally {
        setRobotsBusy(false);
      }
    },
    [client, orgId, id, robotsBusy],
  );

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={styles.heading}>Edit project</h1>

        {state.phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading project…</span>
          </div>
        ) : null}

        {state.phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{state.message}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {state.phase === 'ready' ? (
          <ProjectForm
            key={state.project.id}
            initialValues={{
              name: state.project.name,
              webPropertyUrl: state.project.webPropertyUrl,
              authorizedHost: state.project.authorizedHost,
            }}
            submitLabel="Save changes"
            busyLabel="Saving…"
            onSubmit={onSubmit}
            headerExtra={
              <>
                <InlineAlert severity="info">
                  {state.project.domainVerificationStatus === 'verified' ? (
                    <>
                      Your store domain is verified &mdash; you&rsquo;re ready to run your first
                      scan.{' '}
                      <Link to={`/projects/${state.project.id}/scans`}>Run a scan</Link>
                    </>
                  ) : (
                    <>
                      Next step: verify your store domain so TestCraft can start testing.{' '}
                      <Link to={`/projects/${state.project.id}/domain-verification`}>
                        Verify your store domain
                      </Link>
                    </>
                  )}
                </InlineAlert>
                <div className={styles.badgeRow}>
                  <span className={styles.badgeLabel}>Domain status:</span>
                  <StatusBadge status={state.project.domainVerificationStatus} />
                  <Link
                    to={`/projects/${state.project.id}/domain-verification`}
                    className={styles.badgeLink}
                  >
                    Verify domain
                  </Link>
                  <Link to={`/projects/${state.project.id}/scans`} className={styles.badgeLink}>
                    View scans
                  </Link>
                  <Link
                    to={`/projects/${state.project.id}/journey-health`}
                    className={styles.badgeLink}
                  >
                    Journey health
                  </Link>
                  <Link
                    to={`/projects/${state.project.id}/tracker-settings`}
                    className={styles.badgeLink}
                  >
                    Issue tracker
                  </Link>
                </div>
                {canWrite ? (
                  <div className={styles.badgeRow}>
                    <span className={styles.badgeLabel}>robots.txt:</span>
                    <span>
                      {state.project.robotsPolicy === 'override'
                        ? 'Overridden (ignored)'
                        : 'Honored'}
                    </span>
                    {state.project.robotsPolicy === 'override' ? (
                      <Button
                        variant="secondary"
                        busy={robotsBusy}
                        onClick={() => void setRobotsPolicy('honor')}
                      >
                        Restore honoring
                      </Button>
                    ) : state.project.domainVerificationStatus === 'verified' ? (
                      <Button
                        variant="secondary"
                        busy={robotsBusy}
                        onClick={() => void setRobotsPolicy('override')}
                      >
                        Override for this domain
                      </Button>
                    ) : (
                      <span className={styles.badgeLabel}>
                        Verify your domain to override robots.txt.
                      </span>
                    )}
                  </div>
                ) : null}
                {robotsMessage ? (
                  <InlineAlert severity="info">{robotsMessage}</InlineAlert>
                ) : null}
              </>
            }
          />
        ) : null}
      </Card>
    </AppShell>
  );
}
